import { post, putBlob } from './api.js';
import { encryptItem, encryptFileBytes } from './crypto.js';
import { getConfig } from './config.js';
import { humanSize } from './utils.js';
import { toast } from './dialog.js';

const CONCURRENCY = 2;

let jobs = [];
let trayEl = null;
let completeCallbacks = [];
let processing = false;

function ensureTray() {
  if (trayEl && document.body.contains(trayEl)) return;
  trayEl = document.createElement('div');
  trayEl.className = 'upload-tray';
  trayEl.innerHTML = `
    <div class="upload-tray-header">
      <span class="upload-tray-title">Uploads</span>
      <button class="upload-tray-toggle"><i data-lucide="chevron-down" style="width:16px;height:16px"></i></button>
    </div>
    <div class="upload-tray-body"></div>`;
  document.body.appendChild(trayEl);
  try { lucide.createIcons({ nodes: [trayEl] }); } catch (_) {}

  trayEl.querySelector('.upload-tray-toggle').onclick = () => {
    trayEl.classList.toggle('collapsed');
  };
}

function showTray() {
  ensureTray();
  trayEl.classList.add('visible');
  trayEl.classList.remove('collapsed');
}

function hideTray() {
  if (!trayEl) return;
  trayEl.classList.remove('visible');
}

function renderJobs() {
  if (!trayEl) return;
  const body = trayEl.querySelector('.upload-tray-body');
  const pending = jobs.filter(j => j.status !== 'done' || (Date.now() - (j.doneAt || 0)) < 3000);
  if (!pending.length) { hideTray(); return; }

  body.innerHTML = pending.map((j, idx) => `
    <div class="upload-job ${j.status}" data-idx="${idx}">
      <div class="upload-job-info">
        <span class="upload-job-name" title="${escAttr(j.file.name)}">${escHtml(truncName(j.file.name, 30))}</span>
        <span class="upload-job-size">${humanSize(j.file.size)}</span>
      </div>
      <div class="upload-job-progress"><div class="upload-job-bar" style="width:${j.progress}%"></div></div>
      <div class="upload-job-status">
        ${j.status === 'queued' ? 'Queued' : ''}
        ${j.status === 'encrypting' ? 'Encrypting...' : ''}
        ${j.status === 'uploading' ? j.progress + '%' : ''}
        ${j.status === 'saving' ? 'Saving...' : ''}
        ${j.status === 'done' ? '<i data-lucide="check" style="width:14px;height:14px;color:var(--success)"></i>' : ''}
        ${j.status === 'error' ? `<span class="upload-job-err">${escHtml(j.error)}</span><button class="btn btn-ghost btn-sm upload-retry-btn">Retry</button>` : ''}
      </div>
    </div>`).join('');

  try { lucide.createIcons({ nodes: [body] }); } catch (_) {}

  body.querySelectorAll('.upload-retry-btn').forEach(btn => {
    btn.onclick = (e) => {
      e.stopPropagation();
      const idx = parseInt(btn.closest('.upload-job').dataset.idx);
      const job = jobs[idx];
      if (job) {
        job.status = 'queued';
        job.progress = 0;
        job.error = null;
        renderJobs();
        processQueue();
      }
    };
  });

  // Update title with counts
  const active = jobs.filter(j => !['done', 'error'].includes(j.status)).length;
  const titleEl = trayEl.querySelector('.upload-tray-title');
  if (active > 0) titleEl.textContent = `Uploading (${active})`;
  else titleEl.textContent = 'Uploads';
}

export function enqueueFiles(fileList, { groupId, createGroup, autoGroupName } = {}) {
  const cfg = getConfig() || {};
  const maxBytes = cfg.item_max_bytes || 104857600;
  const files = Array.from(fileList);

  // Reject oversized files
  const oversized = files.filter(f => f.size > maxBytes);
  if (oversized.length) {
    toast(`${oversized.length} file${oversized.length > 1 ? 's' : ''} exceed ${humanSize(maxBytes)} limit`);
  }
  const valid = files.filter(f => f.size <= maxBytes);
  if (!valid.length) return;

  // If createGroup, we'll create the group before processing
  const batchMeta = { groupId, createGroup, autoGroupName, createdGroupId: null };

  for (const file of valid) {
    jobs.push({
      file,
      status: 'queued',
      progress: 0,
      error: null,
      doneAt: null,
      batchMeta,
    });
  }

  showTray();
  renderJobs();
  processQueue();
}

export function onAllComplete(callback) {
  completeCallbacks.push(callback);
}

export function clearCompleteCallbacks() {
  completeCallbacks = [];
}

async function processQueue() {
  if (processing) return;
  processing = true;

  while (true) {
    const active = jobs.filter(j => ['encrypting', 'uploading', 'saving'].includes(j.status));
    if (active.length >= CONCURRENCY) break;

    const next = jobs.find(j => j.status === 'queued');
    if (!next) break;

    processJob(next); // fire and forget, will call processQueue again on completion
    if (active.length + 1 >= CONCURRENCY) break;
  }

  processing = false;
}

async function processJob(job) {
  try {
    // If this batch needs a new group and we haven't created it yet
    if (job.batchMeta.createGroup && !job.batchMeta.createdGroupId) {
      const name = job.batchMeta.autoGroupName || generateAutoName();
      const encrypted = encryptItem(JSON.stringify({ name }));
      const group = await post('/groups', {
        encrypted_blob: encrypted.encrypted_blob,
        wrapped_key: encrypted.wrapped_key,
        nonce: encrypted.nonce,
      });
      job.batchMeta.createdGroupId = group.id;
      // Propagate to all jobs in same batch
      for (const j of jobs) {
        if (j.batchMeta === job.batchMeta) {
          j.batchMeta.groupId = group.id;
        }
      }
    }

    // Step 1: Encrypt
    job.status = 'encrypting';
    renderJobs();

    const arrayBuffer = await job.file.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    const enc = encryptFileBytes(bytes);

    // Step 2: Get presigned URL and upload
    job.status = 'uploading';
    renderJobs();

    let urlResp;
    try {
      urlResp = await post('/items/upload-url', { size_bytes: enc.encrypted_blob.byteLength });
    } catch (err) {
      if (err.message.includes('402') || err.message.includes('balance')) {
        toast('Storage balance exhausted — remaining uploads paused');
        // Pause remaining queued jobs
        for (const j of jobs) {
          if (j.status === 'queued') j.status = 'error';
          if (j.status === 'error' && !j.error) j.error = 'Balance exhausted';
        }
        throw err;
      }
      throw err;
    }

    // Try direct S3 upload first; fall back to API proxy on network failure
    let directFailed = false;
    try {
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.timeout = 30000; // 30s connection timeout
        xhr.open('PUT', urlResp.upload_url);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            job.progress = Math.round((ev.loaded / ev.total) * 100);
            renderJobs();
          }
        };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`));
        xhr.onerror = () => { directFailed = true; reject(new Error('Network error')); };
        xhr.ontimeout = () => { directFailed = true; reject(new Error('Timeout')); };
        xhr.send(enc.encrypted_blob);
      });
    } catch (err) {
      if (!directFailed || !urlResp.proxy_upload) throw err;
      // Fallback: upload through API proxy
      job.progress = 0;
      renderJobs();
      await putBlob(`/items/upload/${urlResp.s3_key}`, enc.encrypted_blob);
    }

    // Step 3: Save item
    job.status = 'saving';
    renderJobs();

    const envelope = JSON.stringify({
      item_type: 'document',
      name: job.file.name,
      filename: job.file.name,
      file_size: job.file.size,
      mime_type: job.file.type || 'application/octet-stream',
      file_wrapped_key: enc.wrapped_key,
      file_nonce: enc.nonce,
    });
    const envelopeEnc = encryptItem(envelope);
    const item = await post('/items', {
      encrypted_blob: envelopeEnc.encrypted_blob,
      wrapped_key: envelopeEnc.wrapped_key,
      nonce: envelopeEnc.nonce,
      item_type: 'encrypted',
      metadata: {},
      size_bytes: enc.encrypted_blob.byteLength,
      file_blob_key: urlResp.s3_key,
    });

    // Step 4: Add to group if needed
    const gid = job.batchMeta.groupId || job.batchMeta.createdGroupId;
    if (gid) {
      await post(`/groups/${gid}/items`, { item_id: item.id });
    }

    job.status = 'done';
    job.progress = 100;
    job.doneAt = Date.now();
    renderJobs();
  } catch (err) {
    job.status = 'error';
    job.error = err.message || 'Upload failed';
    renderJobs();
  }

  // Continue processing queue
  processing = false;
  processQueue();

  // Check if all done
  const remaining = jobs.filter(j => !['done', 'error'].includes(j.status));
  if (remaining.length === 0) {
    for (const cb of completeCallbacks) {
      try { cb(jobs); } catch (_) {}
    }
    // Auto-collapse after 3s
    setTimeout(() => {
      const stillActive = jobs.filter(j => !['done', 'error'].includes(j.status));
      if (stillActive.length === 0 && trayEl) {
        trayEl.classList.add('collapsed');
        // Clean up completed jobs after collapse
        setTimeout(() => {
          jobs = jobs.filter(j => j.status === 'error');
          if (!jobs.length) hideTray();
        }, 300);
      }
    }, 3000);
  }
}

function generateAutoName() {
  const d = new Date();
  const date = d.toISOString().slice(0, 10);
  // Find existing auto-named collections with same date prefix
  // We'll just use a timestamp suffix for uniqueness
  const seq = String(d.getHours() * 60 + d.getMinutes()).padStart(3, '0');
  return `${date}-${seq}`;
}

// Return the created group ID for inline rename
export function getLastCreatedGroupId() {
  for (let i = jobs.length - 1; i >= 0; i--) {
    if (jobs[i].batchMeta?.createdGroupId) return jobs[i].batchMeta.createdGroupId;
  }
  return null;
}

function truncName(name, max) {
  if (name.length <= max) return name;
  const ext = name.lastIndexOf('.');
  if (ext > 0 && name.length - ext < 8) {
    return name.slice(0, max - (name.length - ext) - 3) + '...' + name.slice(ext);
  }
  return name.slice(0, max - 3) + '...';
}

function escHtml(s) {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

function escAttr(s) {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
